import { useEffect, useMemo, useRef, useState } from 'react';
import { CornerDownLeft, Search } from 'lucide-react';
import { useNavigate } from 'react-router';
import { createPortal } from 'react-dom';
import { useT } from '@kts/i18n';
import type { NavGroup } from '../nav';

/** Ctrl+K: bo'limlar bo'yicha tez o'tish. Keyinchalik buyurtma/tovar/xodim qidiruvi qo'shiladi. */
export function CommandPalette({ open, onClose, nav }: { open: boolean; onClose: () => void; nav: NavGroup[] }) {
  const [q, setQ] = useState('');
  const [idx, setIdx] = useState(0);
  const navigate = useNavigate();
  const t = useT('app');
  const input = useRef<HTMLInputElement>(null);

  // Qidiruv joriy tildagi nomlar bo'yicha ishlaydi (kalit emas, ko'rinadigan matn).
  const items = useMemo(() => {
    const all = nav.flatMap((g) => g.items.map((i) => ({ ...i, label: t(i.labelKey), group: t(g.labelKey) })));
    const s = q.trim().toLowerCase();
    return s ? all.filter((i) => i.label.toLowerCase().includes(s) || i.group.toLowerCase().includes(s)) : all;
  }, [nav, q, t]);

  useEffect(() => {
    if (open) {
      setQ('');
      setIdx(0);
      setTimeout(() => input.current?.focus(), 10);
    }
  }, [open]);
  useEffect(() => setIdx(0), [q]);

  if (!open) return null;
  const go = (to: string) => {
    onClose();
    navigate(to);
  };
  return createPortal(
    <div className="kts-overlay" style={{ alignItems: 'flex-start', paddingTop: '12vh' }} onMouseDown={(e) => e.target === e.currentTarget && onClose()}>
      <div className="kts-modal" style={{ width: 'min(620px, 100%)' }} role="dialog" aria-label={t('command.title')}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 10, padding: '14px 18px', borderBottom: '1px solid var(--line)' }}>
          <Search size={18} style={{ color: 'var(--muted)' }} />
          <input
            ref={input}
            value={q}
            onChange={(e) => setQ(e.target.value)}
            placeholder={t('command.placeholder')}
            style={{ flex: 1, border: 'none', outline: 'none', background: 'transparent', fontSize: 15, color: 'var(--text)' }}
            onKeyDown={(e) => {
              if (e.key === 'Escape') onClose();
              if (e.key === 'ArrowDown') {
                e.preventDefault();
                setIdx((i) => Math.min(items.length - 1, i + 1));
              }
              if (e.key === 'ArrowUp') {
                e.preventDefault();
                setIdx((i) => Math.max(0, i - 1));
              }
              if (e.key === 'Enter' && items[idx]) go(items[idx].to);
            }}
          />
          <kbd style={{ fontSize: 11, padding: '2px 6px', border: '1px solid var(--line-strong)', borderRadius: 6, color: 'var(--muted)' }}>Esc</kbd>
        </div>
        <div style={{ maxHeight: 400, overflowY: 'auto', padding: 8 }}>
          {items.length === 0 && <div style={{ padding: 24, textAlign: 'center', color: 'var(--muted)' }}>{t('command.empty')}</div>}
          {items.map((it, i) => (
            <button
              key={it.to}
              type="button"
              className="kts-menu__item"
              data-active={i === idx ? 'true' : undefined}
              onMouseEnter={() => setIdx(i)}
              onClick={() => go(it.to)}
            >
              <it.icon />
              <span style={{ flex: 1 }}>{it.label}</span>
              <span style={{ fontSize: 11.5, color: 'var(--muted)' }}>{it.group}</span>
              {i === idx && <CornerDownLeft size={14} style={{ color: 'var(--muted)' }} />}
            </button>
          ))}
        </div>
      </div>
    </div>,
    document.body,
  );
}
